// Player de áudio das mensagens (DESIGN §5.22): play/pause 40, forma de onda de 32 barras, duração, velocidade 1x/1,5x.
// O áudio vem de rota autenticada da API (`/api/eu/arquivos/:id` ou `/api/equipe/arquivos/:id`), same-origin;
// `useUrlArquivo` (plataforma) devolve a mesma URL no web e um blob: no app nativo.
import { useEffect, useMemo, useRef, useState } from "react";
import { useUrlArquivo } from "@plataforma/midia";
import { cn } from "../lib/cn";
import { formatarDuracao } from "../lib/numeros";
import { Icones } from "./icones";

export interface PropsPlayerAudio {
  url: string;
  duracaoSeg: number;
  /** Balão escuro (mensagem do próprio aluno). */
  sobreTinta?: boolean;
  className?: string;
}

/** Forma de onda determinística a partir da URL (sem decodificar o áudio). */
function barras(semente: string, n = 32): number[] {
  let h = 2166136261;
  for (const c of semente) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return Array.from({ length: n }, () => {
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return 0.3 + ((h >>> 0) % 70) / 100;
  });
}

export function PlayerAudio({ url, duracaoSeg, sobreTinta, className }: PropsPlayerAudio) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [tocando, setTocando] = useState(false);
  const [posicao, setPosicao] = useState(0);
  const [rapido, setRapido] = useState(false);
  const onda = useMemo(() => barras(url), [url]);
  const src = useUrlArquivo(url);

  useEffect(() => {
    if (!src) return;
    const a = new Audio(src);
    a.preload = "none";
    audio.current = a;
    const aoTempo = () => setPosicao(a.currentTime);
    const aoFim = () => {
      setTocando(false);
      setPosicao(0);
    };
    a.addEventListener("timeupdate", aoTempo);
    a.addEventListener("ended", aoFim);
    a.addEventListener("pause", () => setTocando(false));
    a.addEventListener("play", () => setTocando(true));
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", aoTempo);
      a.removeEventListener("ended", aoFim);
      audio.current = null;
    };
  }, [src]);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = rapido ? 1.5 : 1;
  }, [rapido]);

  const alternar = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => setTocando(false));
    else a.pause();
  };

  const progresso = duracaoSeg > 0 ? posicao / duracaoSeg : 0;
  const tocadas = Math.round(progresso * onda.length);
  const restante = tocando || posicao > 0 ? duracaoSeg - posicao : duracaoSeg;

  return (
    <div className={cn("flex h-12 items-center gap-3", className)}>
      <button
        type="button"
        onClick={alternar}
        aria-label={tocando ? "Pausar áudio" : `Tocar áudio de ${Math.round(duracaoSeg)} segundos`}
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-full",
          sobreTinta ? "bg-sobre-tinta text-tinta foco-sobre-tinta" : "bg-tinta text-sobre-tinta",
        )}
      >
        {tocando ? (
          <Icones.pausar size={20} aria-hidden />
        ) : (
          <Icones.tocar size={20} aria-hidden className="ml-0.5" />
        )}
      </button>
      <div aria-hidden className="flex h-8 min-w-0 flex-1 items-center gap-[2px] overflow-hidden">
        {onda.map((altura, i) => (
          <span
            key={i}
            className={cn(
              "w-[3px] shrink-0 rounded-full",
              i < tocadas
                ? sobreTinta
                  ? "bg-sobre-tinta"
                  : "bg-tinta"
                : sobreTinta
                  ? "bg-tinta-2 escuro:bg-sobre-tinta-2"
                  : "bg-fio-controle",
            )}
            style={{ height: `${Math.round(altura * 100)}%` }}
          />
        ))}
      </div>
      <span
        className={cn(
          "w-10 text-right text-rotulo placar",
          sobreTinta ? "text-sobre-tinta-2" : "text-texto-2",
        )}
      >
        {formatarDuracao(restante)}
      </span>
      <button
        type="button"
        onClick={() => setRapido((r) => !r)}
        aria-label={rapido ? "Velocidade 1,5x. Voltar para 1x" : "Velocidade 1x. Acelerar para 1,5x"}
        className={cn(
          "h-8 shrink-0 rounded-celula px-1.5 text-rotulo font-semibold",
          sobreTinta ? "text-sobre-tinta-2 foco-sobre-tinta" : "text-texto-2",
        )}
      >
        {rapido ? "1,5x" : "1x"}
      </button>
    </div>
  );
}
